import React, { useState } from 'react';
import { CalculationResult, WallConfig } from '../types';
import { Hash, Binary } from 'lucide-react';

interface MetricsGridProps {
  results: CalculationResult;
  config: WallConfig;
  setConfig: React.Dispatch<React.SetStateAction<WallConfig>>;
}

const MetricsGrid: React.FC<MetricsGridProps> = ({ results, config, setConfig }) => {
  // For ratios and physical dimensions (always decimal)
  const formatGeneral = (val: number, precision = 2) => {
    return parseFloat(val.toFixed(precision)).toString();
  };

  // For pixel dimensions (affected by toggle)
  const formatPixels = (val: number) => {
    if (!config.showDecimals) {
      return Math.round(val).toString();
    }
    return parseFloat(val.toFixed(2)).toString();
  };

  const getSimplestRatio = (decimal: number): string => {
    if (isNaN(decimal) || decimal <= 0) return "N/A";
    let x = decimal;
    let n1 = 1, d1 = 0;
    let n2 = 0, d2 = 1;
    const limit = 0.0001;
    for (let i = 0; i < 10; i++) {
      let a = Math.floor(x);
      let n_temp = a * n1 + n2;
      let d_temp = a * d1 + d2;
      n2 = n1; d2 = d1;
      n1 = n_temp; d1 = d_temp;
      if (Math.abs(decimal - n1 / d1) < limit) break;
      if (x === a) break;
      x = 1 / (x - a);
    }
    return `${n1}:${d1}`;
  };

  const MetricCard = ({ title, value, unit, color = "text-white" }: { title: string, value: string | number, unit?: string, color?: string }) => (
    <div className="bg-slate-900 border border-slate-800 p-4 rounded-lg hover:border-slate-700 transition-colors shadow-sm">
      <h4 className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-2">{title}</h4>
      <div className="flex items-baseline gap-1">
        <span className={`text-xl font-mono-data font-bold ${color}`}>{value}</span>
        {unit && <span className="text-[10px] text-slate-500 font-bold uppercase">{unit}</span>}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Overview Block */}
      <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
        <MetricCard 
          title="Total Resolution" 
          value={`${formatPixels(results.totalWidth)} × ${formatPixels(results.totalHeight)}`} 
          unit="Pixels" 
          color="text-emerald-400"
        />
        <MetricCard 
          title="Physical Footprint" 
          value={`${formatGeneral(results.physicalWidthM)}m × ${formatGeneral(results.physicalHeightM)}m`} 
          color="text-[#ff8000]"
        />
      </div>

      {/* Actual Ratios - Dynamically based on Source Content */}
      <MetricCard 
        title={`Wall Ratio (${formatGeneral(results.targetW, 0)}:x)`} 
        value={`${formatGeneral(results.targetW, 0)}:${formatGeneral(results.normW_x, 3)}`} 
      />
      <MetricCard 
        title={`Wall Ratio (x:${formatGeneral(results.targetH, 0)})`} 
        value={`${formatGeneral(results.normX_H, 3)}:${formatGeneral(results.targetH, 0)}`} 
      />

      {/* Target Info */}
      <div className="col-span-1 md:col-span-2 p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
         <div className="flex justify-between items-center mb-1">
            <span className="text-[10px] text-blue-400 uppercase font-bold tracking-widest">Target Aspect Details</span>
            <span className="text-[10px] font-mono-data text-blue-300 bg-blue-500/20 px-2 py-0.5 rounded">{getSimplestRatio(results.targetDecimal)} Ratio</span>
         </div>
      </div>

      {/* Fit Mode */}
      <div className="col-span-1 md:col-span-2 border-t border-slate-800 pt-4 mt-2">
        <div className="flex justify-between items-center mb-3">
          <h5 className="text-[11px] font-bold text-blue-400 uppercase tracking-widest flex items-center gap-2">
            Fit Analysis <span className="text-[9px] text-slate-600 font-normal normal-case">(Letterbox/Pillarbox)</span>
          </h5>
          
          {/* Rounding Toggle moved here */}
          <div className="bg-slate-900/50 border border-slate-800 p-0.5 rounded-lg flex gap-0.5 scale-90 origin-right">
            <button
              onClick={() => setConfig(prev => ({ ...prev, showDecimals: true }))}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider transition-all ${
                config.showDecimals 
                  ? 'bg-blue-500 text-white' 
                  : 'text-slate-500 hover:text-slate-300'
              }`}
              title="Show actual decimal values"
            >
              <Hash size={10} />
              Actual
            </button>
            <button
              onClick={() => setConfig(prev => ({ ...prev, showDecimals: false }))}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider transition-all ${
                !config.showDecimals 
                  ? 'bg-blue-500 text-white' 
                  : 'text-slate-500 hover:text-slate-300'
              }`}
              title="Round to nearest whole pixel"
            >
              <Binary size={10} />
              Closest Pixel
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard 
            title="Active Content Area" 
            value={`${formatPixels(results.fitMode.width)} × ${formatPixels(results.fitMode.height)}`} 
            unit="Px" 
          />
          <MetricCard 
            title="Offset (Black Bars)" 
            value={results.fitMode.offsetX > 0 ? `${formatPixels(results.fitMode.offsetX)}px L/R` : `${formatPixels(results.fitMode.offsetY)}px T/B`} 
            color="text-blue-400"
          />
        </div>
      </div>

      {/* Fill Mode */}
      <div className="col-span-1 md:col-span-2 border-t border-slate-800 pt-4 mt-2">
        <h5 className="text-[11px] font-bold text-rose-400 uppercase tracking-widest mb-3 flex items-center gap-2">
          Fill Analysis <span className="text-[9px] text-slate-600 font-normal normal-case">(Uniform Zoom & Crop)</span>
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard 
            title="Canvas size to Fill" 
            value={`${formatPixels(results.fillMode.width)} × ${formatPixels(results.fillMode.height)}`} 
            unit="Px" 
          />
          <MetricCard 
            title="Cropped Pixels" 
            value={results.fillMode.lostW > 0 ? `${formatPixels(results.fillMode.lostW / 2)}px L/R` : `${formatPixels(results.fillMode.lostH / 2)}px T/B`} 
            color="text-rose-400"
          />
        </div>
      </div>
    </div>
  </div>
);
};

export default MetricsGrid;